import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Spinner, Modal } from 'react-bootstrap';
import config from '../config';
import { useUserContext } from '../UserContext';
import { useAuthContext } from '../context/AuthContext';
import { useAppointmentsContext } from '../AppointmentsContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePacienteContext } from '../PacienteContext';
import axios from 'axios';
import { useAlertModal } from '../AlertModalContext';
import logoImage from '../img/logo.png';
import AgendaTab from '../components/consultorio/AgendasCardTab';
import PacientesTab from '../components/consultorio/PacientesTab';
import { useEntidadeContext } from '../EntidadeContext';
import Prontuario from '../components/consultorio/Prontuario';
import OdontologiaTab from '../components/consultorio/OdontologiaTab';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Consultorio = () => {
  const { currentUserRole, getPermittedInternalTabs, isLoadingAccessConfig, dynamicAccessConfig } = useUserContext();
  const { loggedInUser: currentUser, isFetchingSubscription, isSubscriptionValid } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlertModal } = useAlertModal();
  const { currentEntidade, setCurrentEntidade, isLoadingEntidade, agendaConfig, isLoadingAgendaConfig, supportData, isLoadingSupportData, addPacienteToList } = useEntidadeContext();
  const {
    isScreenLocked, setIsScreenLocked,
    pacienteSelecionado, setPacienteSelecionado,
    setFotoAntesProntuario, setFotoDepoisProntuario,
    lastActivePatientContext, setLastActivePatientContext,
    triggerRefreshAppointments
  } = usePacienteContext();

  // --- RESPONSIVIDADE ---
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const isMobile = windowWidth < 576;
  const isTablet = windowWidth >= 576 && windowWidth < 992;
  const isMdDevice = windowWidth >= 768 && windowWidth < 992;

  // --- ESTADOS ---
  const [prontuarioData, setProntuarioData] = useState({ historico: [], consultaAtual: null });
  const lastSetProntuarioDataRef = useRef(null);
  const allAppointmentsFetchedRef = useRef(null);
  const prontuarioFetchedForIdRef = useRef(null);
  const { appointments, setAppointments } = useAppointmentsContext();
  const selectedPatientTabRef = useRef(null);
  const [paciente, setPaciente] = useState({ id: '', nome: '', dataNascimento: '', cpf: '', email: '', agendamento_updated_at: '', agendamento_status: '', duracao_atendimento: '', data_agendamento: '', hora_marcada: '', foto_path: null });
  const [selectedEspecialidade, setSelectedEspecialidade] = useState('');
  const [selectedMedico, setSelectedMedico] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [calendarAppointments, setCalendarAppointments] = useState([]);
  const [isLoadingCalendarData, setIsLoadingCalendarData] = useState(false);
  const [agendaRefreshKey, setAgendaRefreshKey] = useState(0);
  const [ordemDeChegada, setOrdemDeChegada] = useState([]);
  const [isLoadingOrdemDeChegada, setIsLoadingOrdemDeChegada] = useState(false);
  const [key, setKey] = useState(() => location.state?.activeTab || 'agenda');

  // --- INTERCEPTOR ---
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response && error.response.status === 403 && error.response.data?.message?.includes('Sua assinatura não está ativa')) {
          showAlertModal('Assinatura Inativa', `${error.response.data.message} Clique em OK para ser redirecionado à página de planos.`, 'warning', () => navigate('/pricing'));
          return Promise.reject(error);
        }
        return Promise.reject(error);
      }
    );
    return () => { axios.interceptors.response.eject(responseInterceptor); };
  }, [showAlertModal, navigate]);

  // --- AGENDA CONFIG CHECK ---
  useEffect(() => {
    if (!isLoadingEntidade && !isLoadingAgendaConfig && isSubscriptionValid && currentEntidade && currentUserRole === 'Representante Legal') {
      const horariosGerados = currentEntidade.agenda_horarios_gerados;
      const isAgendaConfigurada = horariosGerados && Object.values(horariosGerados).some(dia => (dia['Manhã'] && dia['Manhã'].length > 0) || (dia['Tarde'] && dia['Tarde'].length > 0) || (dia['Noite'] && dia['Noite'].length > 0));
      if (!isAgendaConfigurada) {
        showAlertModal('Configuração da Agenda Pendente', 'Os horários de atendimento da sua clínica ainda não foram definidos. Isso é essencial para usar a agenda. Clique em OK para configurar agora.', 'warning', () => navigate('/configuracoes'));
      }
    }
  }, [currentEntidade, isLoadingEntidade, isLoadingAgendaConfig, isFetchingSubscription, currentUserRole, showAlertModal, navigate]);

  const horariosAtendimento = useMemo(() => {
    if (!currentEntidade?.agenda_horarios_gerados) return { 'Manhã': [], 'Tarde': [], 'Noite': [] };
    const diaDaSemana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sab'][selectedDate.getDay()];
    return currentEntidade.agenda_horarios_gerados[diaDaSemana] || { 'Manhã': [], 'Tarde': [], 'Noite': [] };
  }, [currentEntidade, selectedDate]);

  const totalSlotsDisponiveis = useMemo(() => Object.values(horariosAtendimento).reduce((total, horarios) => total + horarios.length, 0), [horariosAtendimento]);

  const getFeriadoNome = useCallback((date) => {
    if (!date || !agendaConfig.feriados) return null;
    const dateString = date.toISOString().split('T')[0];
    const feriado = agendaConfig.feriados.find(f => f.data === dateString);
    return feriado ? feriado.descricao : null;
  }, [agendaConfig]);

  const handlePatientCreated = useCallback((newPatient) => {
    addPacienteToList(newPatient);
    showAlertModal('Sucesso', `Paciente "${newPatient.nome}" criado com sucesso!`, 'success');
    if (currentEntidade && !currentEntidade.has_pacientes) {
      setCurrentEntidade(prev => ({ ...prev, has_pacientes: true, pacientes_count: (prev.pacientes_count || 0) + 1 }));
    }
  }, [currentEntidade, setCurrentEntidade, showAlertModal, addPacienteToList]);

  const handlePatientUpdate = useCallback((updatedPatient) => {
    showAlertModal('Sucesso', `Paciente "${updatedPatient.nome}" atualizado com sucesso!`, 'success');
  }, [showAlertModal]);

  const handlePatientDeleted = useCallback((deletedPatientId) => {
    showAlertModal('Sucesso', 'Paciente excluído com sucesso!', 'success');
  }, [showAlertModal]);

  const isCurrentUserCorpoClinico = useMemo(() => {
    if (!currentUser || !currentUser.id || !supportData.todosMedicos || supportData.todosMedicos.length === 0) return false;
    return supportData.todosMedicos.some(medico => medico.id === currentUser.id);
  }, [currentUser, supportData.todosMedicos]);

  useEffect(() => {
    if (isCurrentUserCorpoClinico && currentUser?.id && supportData.todosMedicos?.length > 0) {
      const medicoLogado = supportData.todosMedicos.find(m => m.id === currentUser.id);
      if (medicoLogado) {
        setSelectedMedico(medicoLogado.id.toString());
        if (medicoLogado.especialidade_id) setSelectedEspecialidade(medicoLogado.especialidade_id.toString());
      }
    }
  }, [isCurrentUserCorpoClinico, currentUser, supportData.todosMedicos]);

  const isDiaBloqueado = useCallback((date) => {
    if (!date || !agendaConfig.diasBloqueados) return false;
    const dateString = date.toISOString().split('T')[0];
    return agendaConfig.diasBloqueados.some(b => b.data === dateString);
  }, [agendaConfig]);

  const fetchOrdemDeChegada = useCallback(async () => {
    if (!isCurrentUserCorpoClinico || !currentUser?.id || !currentEntidade) { setOrdemDeChegada([]); return; }
    setIsLoadingOrdemDeChegada(true);
    try {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      const response = await axios.get(`/api/medicos/${currentUser.id}/ordem-chegada/${formattedDate}`);
      setOrdemDeChegada(response.data.agendamentos || []);
    } catch (error) {
      setOrdemDeChegada([]);
    } finally {
      setIsLoadingOrdemDeChegada(false);
    }
  }, [isCurrentUserCorpoClinico, currentUser?.id, selectedDate, currentEntidade?.id]);

  useEffect(() => {
    if (selectedMedico) { } else { setCalendarAppointments([]); }
  }, [selectedMedico]);

  const handleAppointmentStatusUpdated = useCallback(async (updatedAppointment) => {
    if (updatedAppointment) {
      if (pacienteSelecionado && updatedAppointment.id === pacienteSelecionado.agendamento_id) {
        if (updatedAppointment.consulta) setProntuarioData(prev => ({ ...prev, consultaAtual: updatedAppointment.consulta }));
        setPacienteSelecionado(prev => {
          const servicosAtuais = prev.servicosDoAgendamento || [];
          const novosServicos = updatedAppointment.consulta.servicos || [];
          const servicosMap = new Map();
          servicosAtuais.forEach(s => servicosMap.set(s.id, s));
          novosServicos.forEach(s => servicosMap.set(s.id, s));
          return { ...prev, servicosDoAgendamento: Array.from(servicosMap.values()) };
        });
      }
      setAgendaRefreshKey(prev => prev + 1);
    } else {
      setAgendaRefreshKey(prev => prev + 1);
      fetchOrdemDeChegada();
    }
  }, [fetchOrdemDeChegada]);

  useEffect(() => {
    if (key === 'agenda' && selectedMedico && currentEntidade?.id && !isLoadingEntidade && currentUserRole) fetchOrdemDeChegada();
  }, [key, selectedMedico, currentEntidade?.id, isLoadingEntidade, currentUserRole, fetchOrdemDeChegada]);

  const fetchProntuarioData = useCallback(async (paciente, options = {}) => {
    const { force = false } = options;
    if (!paciente || !paciente.id || !currentEntidade) {
      if (!paciente) { setProntuarioData({ historico: [], consultaAtual: null }); prontuarioFetchedForIdRef.current = null; }
      return;
    }
    if (!force && prontuarioFetchedForIdRef.current === paciente.id) return;
    prontuarioFetchedForIdRef.current = paciente.id;
    const historicoPromise = paciente.historico ? Promise.resolve({ data: { paciente: { consultas: paciente.historico } } }) : axios.get(`/api/pacientes/${paciente.id}/consultas`);
    const consultaAtualPromise = paciente.consultaAtual ? Promise.resolve({ data: paciente.consultaAtual }) : (paciente.agendamento_id && paciente.agendamento_status === 'Em andamento') ? axios.get(`/api/agendamentos/${paciente.agendamento_id}`) : Promise.resolve({ data: null });
    const [historicoRes, consultaAtualRes] = await Promise.all([historicoPromise, consultaAtualPromise]);
    const sortedConsultas = (historicoRes.data.paciente.consultas || []).sort((a, b) => b.id - a.id);
    let consultaAtualData = consultaAtualRes.data;
    const consultaDoAgendamento = sortedConsultas.find(
      consulta => Number(consulta.agendamento_id) === Number(paciente.agendamento_id)
    );
    if (consultaDoAgendamento && consultaAtualData && !consultaAtualData.agendamento_id) {
      consultaAtualData = { ...consultaDoAgendamento, agendamento: consultaAtualData };
    } else if (consultaDoAgendamento && !consultaAtualData) {
      consultaAtualData = consultaDoAgendamento;
    }
    if (!consultaAtualData && paciente.agendamento_id && paciente.agendamento_status === 'Em andamento') {
      const agendamentoRes = await axios.get(`/api/agendamentos/${paciente.agendamento_id}`);
      consultaAtualData = { agendamento: agendamentoRes.data, queixa_principal: '', observacoes: '', servicos: agendamentoRes.data?.servicos || [], isNew: true };
    }
    if (lastSetProntuarioDataRef.current && JSON.stringify(lastSetProntuarioDataRef.current.historico) === JSON.stringify(sortedConsultas) && JSON.stringify(lastSetProntuarioDataRef.current.consultaAtual) === JSON.stringify(consultaAtualData)) return;
    const newProntuarioData = { historico: sortedConsultas, consultaAtual: consultaAtualData };
    setProntuarioData(newProntuarioData);
    lastSetProntuarioDataRef.current = newProntuarioData;
  }, [currentEntidade?.id]);

  const allPermittedTabs = useMemo(() => getPermittedInternalTabs('consultorio', currentUserRole), [getPermittedInternalTabs, currentUserRole, isLoadingAccessConfig, dynamicAccessConfig]);

  const handleConsultaSaved = useCallback((updatedConsulta) => {
    setProntuarioData(prev => ({ ...prev, consultaAtual: updatedConsulta }));
  }, []);

  const handleConsultaFinalizada = useCallback(() => {
    setPacienteSelecionado(null);
    handleAppointmentStatusUpdated();
  }, [setPacienteSelecionado, handleAppointmentStatusUpdated]);

  const prontuarioComponent = useMemo(() => (
    <Prontuario
      historico={prontuarioData.historico}
      consultaAtual={prontuarioData.consultaAtual}
      servicos={supportData.servicos}
      fetchProntuarioData={fetchProntuarioData}
      servicosDoAgendamento={prontuarioData.consultaAtual?.agendamento?.servicos}
      onAppointmentStatusChange={handleAppointmentStatusUpdated}
      onConsultaSaved={handleConsultaSaved}
      onConsultaFinalizada={handleConsultaFinalizada}
      setIsScreenLocked={setIsScreenLocked}
      isActive={key !== 'odontologia'}
    />
  ), [key, prontuarioData, supportData.servicos, fetchProntuarioData, handleAppointmentStatusUpdated, handleConsultaSaved, handleConsultaFinalizada]);

  const renderedTabs = useMemo(() => {
    const tabs = allPermittedTabs.filter(tabInfo => {
      if (tabInfo.key === 'agenda') return !pacienteSelecionado;
      if (tabInfo.key === 'pacientes') return !pacienteSelecionado;
      const activeConsultationTabs = ['prontuario', 'historico', 'anamnese', 'laudo', 'relatorio', 'exames', 'pedidos', 'atestado', 'conferencia'];
      if (activeConsultationTabs.includes(tabInfo.key)) return pacienteSelecionado && pacienteSelecionado.agendamento_status === 'Em andamento';
      return false;
    });
    const activeModules = currentEntidade?.modulos_ativos;
    const odontologiaDisponivel = !Array.isArray(activeModules) || activeModules.includes('odontologia');
    if (odontologiaDisponivel && currentEntidade?.odontologia_habilitada && pacienteSelecionado?.agendamento_status === 'Em andamento') {
      tabs.push({ key: 'odontologia', title: 'Odontologia' });
    }
    return tabs;
  }, [allPermittedTabs, pacienteSelecionado, currentEntidade?.odontologia_habilitada, currentEntidade?.modulos_ativos]);

  useEffect(() => {
    const pacienteId = pacienteSelecionado?.id ?? null;
    const pacienteMudou = selectedPatientTabRef.current !== pacienteId;

    if (pacienteId) {
      fetchProntuarioData(pacienteSelecionado);
      if (pacienteMudou) setKey('prontuario');
    } else {
      fetchProntuarioData(null);
    }

    selectedPatientTabRef.current = pacienteId;
  }, [pacienteSelecionado?.id, fetchProntuarioData]);

  const handleRetomarAtendimento = () => {
    if (lastActivePatientContext) {
      setPacienteSelecionado(lastActivePatientContext);
      setLastActivePatientContext(null);
      setKey('prontuario');
    }
  };

  const handlePatientInterestsUpdate = (pacienteId, novosInteresses) => {};

  // ── Ícones das abas ──────────────────────────────────────────────────────
  const tabIcons = {
    agenda: 'calendar-alt',
    pacientes: 'users',
    prontuario: 'file-medical',
    historico: 'history',
    anamnese: 'clipboard-list',
    laudo: 'file-alt',
    relatorio: 'chart-bar',
    exames: 'microscope',
    pedidos: 'file-prescription',
    atestado: 'certificate',
    conferencia: 'check-double',
    odontologia: 'tooth',
  };

  return (
    <>
      {/* ── Wrapper principal ── */}
      <div style={{
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
        fontFamily: "'Inter', 'Segoe UI', sans-serif",
      }}>

        {/* ── Header moderno ── */}
        <div style={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '64px',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}>

          {/* Lado esquerdo — Logo ou Paciente */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
            {currentUserRole === 'Médico' && pacienteSelecionado ? (
              // Paciente em atendimento
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {/* Foto */}
                {pacienteSelecionado.foto_path ? (
                  <img
                    src={`${config.apiBaseURL}/storage/${pacienteSelecionado.foto_path}`}
                    alt="Foto do Paciente"
                    style={{
                      width: '44px', height: '44px', borderRadius: '50%',
                      objectFit: 'cover', border: '2px solid #e5e7eb', flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%',
                    backgroundColor: '#eff6ff', border: '2px solid #bfdbfe',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '16px', fontWeight: '700', color: '#3b82f6', flexShrink: 0,
                  }}>
                    {pacienteSelecionado.nome?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}

                {/* Info do paciente */}
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: `calc(15px * var(--font-scale))`, fontWeight: '700', color: '#111827', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '200px' }}>
                      {pacienteSelecionado.nome}
                    </span>
                    {pacienteSelecionado.agendamento_status === 'Em andamento' && (
                      <span style={{
                        fontSize: `calc(11px * var(--font-scale))`, fontWeight: '600', padding: '2px 8px', borderRadius: '999px',
                        backgroundColor: '#dcfce7', color: '#16a34a', whiteSpace: 'nowrap',
                      }}>
                        ● Em andamento
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: `calc(12px * var(--font-scale))`, color: '#9ca3af', display: 'flex', gap: '8px', marginTop: '1px' }}>
                    {pacienteSelecionado.email && <span>{pacienteSelecionado.email}</span>}
                    {pacienteSelecionado.cpf && <span>CPF: {pacienteSelecionado.cpf}</span>}
                    {pacienteSelecionado.agendamento_status === 'Em andamento' && pacienteSelecionado.data_agendamento && pacienteSelecionado.hora_marcada && (
                      <span style={{ color: '#3b82f6' }}>
                        {pacienteSelecionado.data_agendamento.split('-').reverse().join('/')} às {pacienteSelecionado.hora_marcada.slice(0, 5)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              // Logo + título
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  <FontAwesomeIcon icon="calendar-alt" style={{ color: '#fff', fontSize: '16px' }} />
                </div>
                <div>
                  <h1 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' }}>
                    Consultório
                  </h1>
                  {currentEntidade && (
                    <p style={{ margin: 0, fontSize: `calc(11px * var(--font-scale))`, color: '#9ca3af' }}>
                      {currentEntidade.nome}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Lado direito — Ações */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
            {/* Botão Retomar Atendimento */}
            {currentUserRole === 'Médico' && !pacienteSelecionado && lastActivePatientContext && (
              <button
                onClick={handleRetomarAtendimento}
                style={{
                  padding: '8px 16px',
                  background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  cursor: 'pointer', fontSize: `calc(13px * var(--font-scale))`, fontWeight: '600',
                  display: 'flex', alignItems: 'center', gap: '6px',
                  boxShadow: '0 1px 3px rgba(14,165,233,0.4)',
                  transition: 'all 0.15s',
                  whiteSpace: 'nowrap',
                }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-1px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}
              >
                <FontAwesomeIcon icon="play-circle" />
                Retomar: {lastActivePatientContext.nome.split(' ')[0]}
              </button>
            )}
          </div>
        </div>

        {/* ── Navegação por abas ── */}
        <div style={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #e5e7eb',
          padding: '0 24px',
          display: 'flex',
          gap: '4px',
          overflowX: 'auto',
        }}>
          {renderedTabs.map(tabInfo => (
            <button
              key={tabInfo.key}
              onClick={() => setKey(tabInfo.key)}
              style={{
                padding: isMobile ? '10px 12px' : '12px 16px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: `calc(13px * var(--font-scale))`,
                fontWeight: key === tabInfo.key ? '600' : '400',
                color: key === tabInfo.key ? '#3b82f6' : '#6b7280',
                borderBottom: key === tabInfo.key ? '2px solid #3b82f6' : '2px solid transparent',
                marginBottom: '-1px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.15s',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}
              onMouseEnter={e => { if (key !== tabInfo.key) e.currentTarget.style.color = '#374151'; }}
              onMouseLeave={e => { if (key !== tabInfo.key) e.currentTarget.style.color = '#6b7280'; }}
            >
              <FontAwesomeIcon
                icon={tabIcons[tabInfo.key] || 'circle'}
                style={{ fontSize: `calc(12px * var(--font-scale))` }}
              />
              {!isMobile && tabInfo.title}
              {isMobile && (
                <span style={{ fontSize: `calc(11px * var(--font-scale))` }}>{tabInfo.title}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── Conteúdo das abas ── */}
        <div style={{ padding: isMobile ? '8px' : '0' }}>
          {renderedTabs.map(tabInfo => {
            let tabContent = null;

            if (tabInfo.key === 'agenda') {
              tabContent = (
                <AgendaTab
                  isActive={key === tabInfo.key}
                  especialidades={supportData.especialidades}
                  todosMedicos={supportData.todosMedicos}
                  allFuncionarios={supportData.allFuncionarios}
                  servicos={supportData.servicos}
                  pacientesList={supportData.pacientesList}
                  selectedEspecialidade={selectedEspecialidade}
                  setSelectedEspecialidade={setSelectedEspecialidade}
                  selectedMedico={selectedMedico}
                  setSelectedMedico={setSelectedMedico}
                  selectedDate={selectedDate}
                  setSelectedDate={setSelectedDate}
                  calendarAppointments={calendarAppointments}
                  isLoadingCalendarData={isLoadingCalendarData}
                  horariosAtendimento={horariosAtendimento}
                  totalSlotsDisponiveis={totalSlotsDisponiveis}
                  getFeriadoNome={getFeriadoNome}
                  isDiaBloqueado={isDiaBloqueado}
                  agendaHorariosGerados={currentEntidade?.agenda_horarios_gerados}
                  onAppointmentCreated={handleAppointmentStatusUpdated}
                  onAppointmentStatusChange={handleAppointmentStatusUpdated}
                  onAppointmentFinalized={handleAppointmentStatusUpdated}
                  refreshKey={agendaRefreshKey}
                  isCurrentUserCorpoClinico={isCurrentUserCorpoClinico}
                  ordemDeChegada={ordemDeChegada}
                  isLoadingOrdemDeChegada={isLoadingOrdemDeChegada}
                  onPatientInterestsUpdate={handlePatientInterestsUpdate}
                  onPatientCreated={handlePatientCreated}
                />
              );
            } else if (tabInfo.key === 'pacientes') {
              tabContent = (
                <PacientesTab
                  pacientesList={supportData.pacientesList}
                  onPatientCreated={handlePatientCreated}
                  onPatientUpdated={handlePatientUpdate}
                  onPatientDeleted={handlePatientDeleted}
                />
              );
            } else if (tabInfo.key === 'odontologia') {
              tabContent = <OdontologiaTab
                paciente={pacienteSelecionado}
                consultaAtual={prontuarioData.consultaAtual}
                servicos={supportData.servicos}
                profissionais={supportData.todosMedicos}
                especialidades={supportData.especialidades}
              />;
            } else if (['prontuario', 'historico', 'alergias', 'laudos', 'documentos_historico', 'receituario', 'atestado'].includes(tabInfo.key)) {
              if (prontuarioData.consultaAtual) {
                tabContent = prontuarioComponent;
              }
            }

            if (tabContent === null) {
              tabContent = (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', gap: '12px' }}>
                  <Spinner animation="border" variant="primary" size="sm" />
                  <span style={{ color: '#9ca3af', fontSize: `calc(14px * var(--font-scale))` }}>Carregando "{tabInfo.title}"...</span>
                </div>
              );
            }

            // Mantém agenda e prontuário montados
            const shouldKeepMounted = tabInfo.key === 'prontuario' || tabInfo.key === 'agenda';

            return (
              <div
                key={tabInfo.key}
                style={{
                  display: key === tabInfo.key ? 'block' : (shouldKeepMounted ? 'none' : 'none'),
                }}
              >
                {(key === tabInfo.key || shouldKeepMounted) && tabContent}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Modal de Bloqueio de Tela ── */}
      <Modal
        show={isScreenLocked}
        onHide={() => {}}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Body style={{ textAlign: 'center', padding: '40px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Spinner animation="border" style={{ color: '#fff', width: '32px', height: '32px', borderWidth: '3px' }} />
          </div>
          <h5 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: '#111827' }}>
            Processando...
          </h5>
          <p style={{ margin: 0, fontSize: `calc(13px * var(--font-scale))`, color: '#9ca3af' }}>
            Por favor, aguarde.
          </p>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Consultorio;
